import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "src/components/app/layout/Header";
import MedicationChart from "src/components/app/insights/MedicationChart";
import InsightsPopover from "src/components/app/InsightsPopover";
import LogHistory from "src/components/app/log-details/LogHistory";
import LogTrends from "src/components/app/log-details/LogTrends";
import Tabs from "src/components/app/Tabs";
import Dropdown from "src/components/common/Dropdown";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as medicationActions from "src/redux/slices/medicationSlice";
import * as reminderActions from "src/redux/slices/reminder";

interface Medication {
  _id: string;
  name: string;
}

const screenTabs = [
  { id: "trends", title: "Trends" },
  { id: "history", title: "History" },
];

const ChartTypes = [{ name: "W", value: "week" }];

const MedicationLogDetailsScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const { patient } = useAppSelector((state) => state.auth);
  const {
    medicationLog,
    medicationsList,
    addMedication,
    editMedication,
    deleteMedication,
  } = useAppSelector((state) => state.medication);

  const queryParams = new URLSearchParams(location.search);
  const logDetailsScreenKey = queryParams.get("log");
  const medicationIdFromUrl = queryParams.get("medication_id");

  const [activeTab, setActiveTab] = useState<string>("trends");
  const [activeFilter, setActiveFilter] = useState<string>("week");
  const [medicationOptions, setMedicationOptions] = useState<Medication[]>([]);
  const [medicationType, setMedicationType] = useState<string | number>("");

  useEffect(() => {
    const getMedicationList = async () => {
      setMedicationType("");
      new Promise((resolve, reject) => {
        dispatch(
          medicationActions.getMedicationListRequest({ resolve, reject }),
        );
      }).then((res: any) => {
        if (res?.data?.length) {
          setMedicationOptions(res.data);
          const fromUrl =
            medicationIdFromUrl &&
            res.data.some((med: any) => med._id === medicationIdFromUrl)
              ? medicationIdFromUrl
              : null;
          const currentMedicationExists = res.data.some(
            (med: any) => med._id === medicationType,
          );
          if (fromUrl) {
            setMedicationType(fromUrl);
          } else if (currentMedicationExists) {
            setMedicationType(medicationType);
          } else {
            const newMedicationId = res.data[0]._id || "";
            setMedicationType(newMedicationId);
          }
        } else {
          setMedicationOptions([]);
          setMedicationType("");
        }
      });
    };

    getMedicationList();
  }, [addMedication, editMedication, deleteMedication, medicationIdFromUrl]);

  const fetchLogData = useCallback(async () => {
    if (!patient?.data?.patient_id || medicationOptions.length === 0) return;

    const selectedMedicationId =
      medicationType || medicationsList?.data?.[0]?._id;

    // Validate that the selected medication ID exists in current options
    const isValidMedication = medicationOptions.some(
      (med) => med._id === selectedMedicationId,
    );

    if (!isValidMedication) return;

    const params = {
      user_id: patient.data.patient_id,
      filter_type: activeFilter,
      logger: "medication",
      medication_id: selectedMedicationId,
    };

    const res: any = await new Promise((resolve, reject) => {
      dispatch(
        medicationActions.getMedicationLogRequest({
          queryParams: params,
          resolve,
          reject,
        }),
      );
    });
  }, [
    activeFilter,
    medicationType,
    medicationsList,
    patient?.data?.patient_id,
    medicationOptions,
  ]);

  useEffect(() => {
    if (medicationType && medicationOptions.length > 0) {
      fetchLogData();
    }
  }, [medicationType, medicationOptions, fetchLogData]);

  // Fetch reminders when component mounts
  useEffect(() => {
    if (patient?.data?.patient_id) {
      new Promise((resolve, reject) => {
        dispatch(
          reminderActions.getReminderRequest({
            resolve,
            reject,
            payload: {
              user_id: patient?.data?.patient_id,
            },
          }),
        );
      }).catch((error) => {
        console.error("Error fetching reminders:", error);
      });
    }
  }, [dispatch, patient?.data?.patient_id]);

  const dropdownOptions = useMemo(
    () =>
      medicationOptions.map((medication) => ({
        value: medication._id,
        label: medication.name,
      })),
    [medicationOptions],
  );

  const renderTabs = () => (
    <Tabs
      tabs={screenTabs}
      activeTab={activeTab}
      onChange={(tab) => setActiveTab(tab.id)}
    />
  );

  const renderFilterButtons = () =>
    ChartTypes.map((type) => (
      <button
        key={type.value}
        type="button"
        className={`px-2.5 py-1 text-sm font-normal rounded-md transition-colors text-black ${
          activeFilter === type.value
            ? "bg-[var(--app-primary)] text-white"
            : ""
        }`}
        onClick={() => setActiveFilter(type.value)}
      >
        {type.name}
      </button>
    ));

  return (
    <div className="flex flex-col gap-4">
      <div className="relative h-[calc(100vh-75px)] overflow-y-auto">
        {/* Header */}
        <div className="fixed top-0 left-0 right-0 z-10 max-w-lg mx-auto">
          <Header
            titlePosition="center"
            title="Medication Logs"
            onBackClick={() => navigate(-1)}
            rightComponent={
              <InsightsPopover
                type={logDetailsScreenKey ?? "medication-logs"}
              />
            }
          />
          <div className="p-4 space-y-4 bg-white border-b border-gray-200">
            {renderTabs()}
          </div>
        </div>

        {/* Content */}
        <div className="w-full mt-32">
          {activeTab === "trends" && (
            <LogTrends
              className="h-64"
              logDetailsScreenKey={logDetailsScreenKey}
              chartTitle="Medication Logs"
              chartSection={
                <MedicationChart
                  data={medicationLog?.data?.medication_schedule}
                />
              }
              isFilters
              filterSection={renderFilterButtons()}
              selectionSection={
                <div className="w-full flex-1">
                  <Dropdown
                    name="medicationType"
                    options={dropdownOptions}
                    value={medicationType}
                    onChange={setMedicationType}
                    fullWidth
                  />
                </div>
              }
            />
          )}

          {activeTab === "history" && (
            <LogHistory logDetailsScreenKey={logDetailsScreenKey || ""} />
          )}
        </div>
      </div>
    </div>
  );
};

export default MedicationLogDetailsScreen;
